import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Collapse, Modal, Tag, Typography, theme } from 'antd';
import type { ReleaseNoteItem, ReleaseNoteType, ReleaseNotesRelease } from './types';

const { Text } = Typography;

const TYPE_ORDER: ReleaseNoteType[] = ['new', 'improved', 'fixed'];

const TYPE_META: Record<ReleaseNoteType, { label: string; color: string }> = {
  new: { label: 'New', color: 'success' },
  improved: { label: 'Improved', color: 'processing' },
  fixed: { label: 'Fixed', color: 'default' },
};

function formatDate(iso?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function countSummary(items: ReleaseNoteItem[]): string {
  return TYPE_ORDER
    .map((type) => {
      const count = items.filter((item) => item.type === type).length;
      if (!count) return '';
      return `${count} ${TYPE_META[type].label.toLowerCase()}`;
    })
    .filter(Boolean)
    .join(' · ');
}

interface Props {
  open: boolean;
  appName: string;
  mode: 'unseen' | 'history';
  releases: ReleaseNotesRelease[];
  onClose: () => void;
}

export default function ReleaseNotesModal({ open, appName, mode, releases, onClose }: Props) {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const newest = releases[0];
  const several = releases.length > 1;

  const headerDetail = useMemo(() => {
    if (!newest) return "You're up to date.";
    if (mode === 'history') return several ? `${releases.length} releases` : `Version ${newest.version}`;
    if (several) return `${releases.length} releases since you last looked`;
    const when = formatDate(newest.releasedAt);
    return when ? `Version ${newest.version} · ${when}` : `Version ${newest.version}`;
  }, [newest, several, releases.length, mode]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width="min(720px, calc(100vw - 32px))"
      centered
      destroyOnClose
      zIndex={1200}
      title={(
        <div>
          <div style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.3 }}>What's new in {appName}</div>
          <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>{headerDetail}</Text>
        </div>
      )}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Reopen this any time from the version in the sidebar.</Text>
          <Button type="primary" onClick={onClose}>Got it</Button>
        </div>
      )}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto', paddingTop: 8 } }}
    >
      {releases.length === 0 ? (
        <div style={{ padding: '28px 8px', textAlign: 'center' }}>
          <Text type="secondary">You're up to date.</Text>
        </div>
      ) : (
        <Collapse
          bordered={false}
          defaultActiveKey={newest ? [newest.version] : []}
          style={{ background: 'transparent' }}
          items={releases.map((release) => ({
            key: release.version,
            label: (
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                <Text strong>v{release.version}</Text>
                {formatDate(release.releasedAt) && <Text type="secondary">{formatDate(release.releasedAt)}</Text>}
                <Text type="secondary" style={{ marginLeft: 'auto' }}>{countSummary(release.items)}</Text>
              </div>
            ),
            children: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {release.items.map((item) => (
                  <div key={item.id} style={{ display: 'flex', gap: 12, padding: '10px 12px', borderRadius: token.borderRadiusLG, background: token.colorFillQuaternary }}>
                    <Tag color={TYPE_META[item.type].color} style={{ margin: 0 }}>{TYPE_META[item.type].label}</Tag>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <Text strong>{item.title}</Text>
                        {item.link && item.link.startsWith('/') && (
                          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => { onClose(); navigate(item.link as string); }}>
                            Open
                          </Button>
                        )}
                      </div>
                      <Text type="secondary" style={{ display: 'block', marginTop: 2 }}>{item.summary}</Text>
                    </div>
                  </div>
                ))}
              </div>
            ),
          }))}
        />
      )}
    </Modal>
  );
}
